import React, {useEffect, useRef, useState} from 'react';
import Header from '../../../components/layout/headers/Header';
import DynamicNavigation from "../../../components/layout/navigation/DynamicNavigation";
import AppCard from '../../../components/ui/AppCard';
import InfoField from '../../../components/ui/InfoField';
import {BlockUI} from 'primereact/blockui';
import {Toast} from 'primereact/toast';
import {Tag} from 'primereact/tag';
import {useHistory, useParams} from 'react-router-dom';
import {getAllClaims, getClaimDocumentTypes, trackClaims, uploadClaimDocument} from '../../../services/claims-service';
import DragDropUploader from '../components/DragDropUploader';

const ViewClaim = () => {
    const { referenceNumber } = useParams();
    const history = useHistory();
    const toast = useRef(null);

    const [loading, setLoading] = useState(true);
    const [claimDetails, setClaimDetails] = useState(null);
    const [claimTracking, setClaimTracking] = useState(null);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [uploadingDoc, setUploadingDoc] = useState(null);

    useEffect(() => {
        if (referenceNumber) {
            fetchClaimData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referenceNumber]);

    const fetchClaimData = async () => {
        setLoading(true);
        try {
            const [claimsRes, trackRes, docTypesRes] = await Promise.all([
                getAllClaims(), // Could use getMyClaims() but if it's admin, getAllClaims is safer for any ref
                trackClaims(),
                getClaimDocumentTypes()
            ]);

            let myClaim = null;
            if (claimsRes.data && claimsRes.data.status === 'SUCCESS') {
                const all = claimsRes.data.responseBody || [];
                myClaim = all.find(c => c.referenceNumber === referenceNumber);
            }

            let myTrack = null;
            if (trackRes.data && trackRes.data.status === 'SUCCESS') {
                const tracks = trackRes.data.responseBody || [];
                myTrack = tracks.find(t => t.referenceNumber === referenceNumber);
            }

            if (docTypesRes.data && docTypesRes.data.status === 'SUCCESS') {
                setDocumentTypes(docTypesRes.data.responseBody || []);
            }

            if (myClaim) {
                setClaimDetails(myClaim);
            }
            if (myTrack) {
                setClaimTracking(myTrack);
            }

            if (!myClaim) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Claim not found.', life: 3000 });
            }
        } catch (error) {
            console.error('Error fetching claim:', error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load claim data.', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (file, docType) => {
        if (!file) return;

        setUploadingDoc(docType);
        try {
            const response = await uploadClaimDocument(referenceNumber, docType, file);
            if (response.data && response.data.status === 'SUCCESS') {
                toast.current.show({ severity: 'success', summary: 'Success', detail: `${docType.replace('_', ' ')} uploaded successfully.`, life: 3000 });
                // refresh tracking to get new URLs
                await fetchClaimData();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: response.data?.message || 'Upload failed.', life: 3000 });
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to upload document.', life: 3000 });
        } finally {
            setUploadingDoc(null);
            const input = document.getElementById(`file-${docType}`);
            if (input) input.value = null;
        }
    };

    const getStatusTone = (statusValue) => {
        if (!statusValue) return "warning";
        const value = statusValue.toLowerCase();
        if (value.includes("approved") || value.includes("paid")) return "success";
        if (value.includes("reject") || value.includes("denied")) return "danger";
        return "warning";
    };

    return (
        <>
            <Header />
            <DynamicNavigation />
            <Toast ref={toast} />
            <div className="min-h-screen bg-base-200 pt-28 pb-8">
                <div className="container mx-auto px-4 max-w-7xl">
                    <BlockUI blocked={loading}>
                        <div className="w-full flex flex-col gap-6">
                            <div>
                                <button className="btn btn-ghost text-primary" onClick={() => history.goBack()}>
                                    <i className="pi pi-arrow-left mr-2"></i>
                                    Back to Claims
                                </button>
                            </div>

                            {/* Main Layout Grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                                
                                {/* 1. Details */}
                                <div>
                                    {claimDetails && (
                                        <AppCard title="Claim Details" className="h-full">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <InfoField label="Status" value={<Tag value={claimDetails.claimStatus} severity={getStatusTone(claimDetails.claimStatus)} />} />
                                                <InfoField label="Policy Number" value={claimDetails.policyNumber} />
                                                <InfoField label="Date Submitted" value={new Date(claimDetails.createdAt).toLocaleString()} />
                                                <InfoField label="Notes" value={claimTracking?.notes || "No notes provided"} className="md:col-span-2" />
                                            </div>
                                        </AppCard>
                                    )}
                                </div>

                                {/* 2. Deceased */}
                                <div>
                                    {claimDetails && (
                                        <AppCard title="Deceased Details" className="h-full">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <InfoField label="First Name" value={claimDetails.deceased?.firstName} />
                                                <InfoField label="Last Name" value={claimDetails.deceased?.lastName} />
                                                <InfoField label="National ID" value={claimDetails.deceased?.nationalId} />
                                            </div>
                                        </AppCard>
                                    )}
                                </div>

                                {/* 3. Claimant */}
                                <div className="col-span-1 lg:col-span-2">
                                    {claimDetails && (
                                        <AppCard title="Claimant Details">
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <InfoField label="First Name" value={claimDetails.claimant?.firstName} />
                                                <InfoField label="Last Name" value={claimDetails.claimant?.lastName} />
                                                <InfoField label="National ID" value={claimDetails.claimant?.nationalId} />
                                                <InfoField label="Relationship" value={claimDetails.claimant?.relationshipToDeceased} />
                                                <InfoField label="Email" value={claimDetails.claimant?.email} />
                                                <InfoField label="Mobile" value={claimDetails.claimant?.mobileNumber} />
                                            </div>
                                        </AppCard>
                                    )}
                                </div>

                                {/* 4. Documents */}
                                <div className="col-span-1 lg:col-span-2">
                                    {claimTracking && (
                                        <AppCard title="Documents">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                                {documentTypes.map((docType) => {
                                                    const uploadedDoc = claimTracking.documents?.find(d => d.type === docType);
                                                    
                                                    return (
                                                        <div className="card bg-base-50 shadow-sm border border-base-200 h-full" key={docType}>
                                                            <div className="card-body items-center text-center p-4">
                                                                <h3 className="card-title text-sm tracking-wide mb-2 uppercase">{docType.replace('_', ' ')}</h3>
                                                                
                                                                {uploadedDoc ? (
                                                                    <>
                                                                        <Tag value="Uploaded" severity="success" className="mb-4" />
                                                                        
                                                                        <figure className="w-full px-2">
                                                                            <a href={uploadedDoc.url} target="_blank" rel="noopener noreferrer" className="block w-full">
                                                                                <div className="relative h-32 bg-base-200 overflow-hidden rounded-xl border border-base-300 cursor-pointer group">
                                                                                    {uploadedDoc.url && (
                                                                                        <img src={uploadedDoc.url} alt="Preview" className="w-full h-full object-cover" />
                                                                                    )}
                                                                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                                                        <span className="text-white font-bold text-sm">Open Document</span>
                                                                                    </div>
                                                                                </div>
                                                                            </a>
                                                                        </figure>
                                                                        
                                                                        <div className="card-actions mt-4 w-full flex-col gap-2">
                                                                            <input type="file" id={`file-${docType}`} className="hidden" accept="image/*,.pdf" onChange={(e) => handleFileUpload(e.target.files[0], docType)} />
                                                                            <button 
                                                                                className="btn btn-outline btn-primary btn-sm w-full rounded-full" 
                                                                                onClick={() => document.getElementById(`file-${docType}`).click()} 
                                                                                disabled={uploadingDoc === docType}
                                                                            >
                                                                                <i className="pi pi-upload mr-2"></i>
                                                                                {uploadingDoc === docType ? "Uploading..." : "Replace"}
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Tag value="Missing" severity="warning" className="mb-4" />
                                                                        <div className="w-full h-full flex flex-col justify-center">
                                                                            <DragDropUploader 
                                                                                accept="image/*,.pdf" 
                                                                                maxFileSize={5000000} 
                                                                                onFileSelect={(file) => handleFileUpload(file, docType)} 
                                                                            />
                                                                            {uploadingDoc === docType && <div className="mt-4 text-primary text-sm font-semibold">Uploading...</div>}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </AppCard>
                                    )}
                                </div>
                            </div>
                        </div>
                    </BlockUI>
                </div>
            </div>
        </>
    );
};

export default ViewClaim;
